import { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
// TODO(@dvgup/branding migration): replace with `@dvgup/branding`'s
// runtime export once TODO-013 lands. The current fixture supplies
// the `share.*` slot consumed by `<SeoHead>` below.
import { brandingFixture } from '../lib/branding/branding-fixture';
import { useAppDispatch } from '../store/hooks';
import {
  authSignInFailed,
  authSignedIn,
} from '../store/slices/auth-slice';
import { getCognitoConfig } from '../lib/auth/cognito-config';
import { decodeJwt } from '../lib/auth/jwt-decoder';
import {
  clearCodeVerifier,
  loadCodeVerifier,
  storeTokens,
} from '../lib/auth/token-storage';
import { getProfile } from '../lib/api/profile-api-client';

/**
 * OAuth-callback surface (task 7.6, public-website spec).
 *
 * `<OAuthCallbackPage>` at route `/oauth-callback` finishes the
 * Cognito Hosted UI authorization-code-with-PKCE flow per
 * **design §2.3**:
 *
 *   1. Read the `code` query parameter from the URL.
 *   2. Load the PKCE code verifier `<SignInPage>` stored in
 *      sessionStorage before the redirect. If absent, the user
 *      arrived directly at `/oauth-callback` without going through
 *      `/sign-in`; dispatch `authSignInFailed` with
 *      `error: 'missing-verifier'` and surface an error message.
 *   3. POST `application/x-www-form-urlencoded` to the Hosted UI's
 *      `/oauth2/token` endpoint with `grant_type=authorization_code`,
 *      `client_id`, `code`, `redirect_uri`, `code_verifier`. Cognito
 *      responds with `id_token`, `access_token`, `expires_in`, and
 *      (optionally) `refresh_token`.
 *   4. Decode the ID token (no signature verification — the API
 *      Gateway authorizer performs that check) to extract `sub`.
 *   5. Persist the token record in sessionStorage and dispatch
 *      `authSignedIn` so the auth slice flips to `'authenticated'`.
 *   6. Clear the code verifier — single-use per RFC 7636.
 *   7. Call `getProfile(idToken)` (the task 7.6 stub; replaced by
 *      the real `ProfileApi.GetProfileV1` client in task 8.1) to
 *      determine the post-sign-in route per **Requirement 5.7**:
 *      `exists === true` → navigate to `/`; `exists === false` →
 *      navigate to `/sign-up`.
 *
 * Any throw or rejection along the way short-circuits to
 * `authSignInFailed` + an error message; the page never advances
 * past the failure state silently.
 *
 * Function-length budget: every export below stays under 20 lines
 * per Requirement 3.7. The async pipeline factors into
 * `runOAuthCallback` so the React effect itself stays compact.
 */

interface CognitoTokenResponse {
  readonly id_token: string;
  readonly access_token: string;
  readonly expires_in: number;
  readonly refresh_token?: string;
}

interface ExchangeArgs {
  readonly hostedUiUrl: string;
  readonly userPoolClientId: string;
  readonly code: string;
  readonly redirectUri: string;
  readonly codeVerifier: string;
}

/**
 * POST the authorization code + PKCE verifier to the Hosted UI's
 * `/oauth2/token` endpoint and return the parsed token response.
 *
 * Throws when the response is non-2xx so the caller can branch on
 * the throw to dispatch `authSignInFailed`.
 */
export async function exchangeCodeForTokens(
  args: ExchangeArgs,
): Promise<CognitoTokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: args.userPoolClientId,
    code: args.code,
    redirect_uri: args.redirectUri,
    code_verifier: args.codeVerifier,
  });
  const response = await fetch(`${args.hostedUiUrl}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!response.ok) {
    throw new Error(`token-exchange-failed-${response.status}`);
  }
  return (await response.json()) as CognitoTokenResponse;
}

interface CallbackPipelineArgs {
  readonly code: string;
  readonly hostedUiUrl: string;
  readonly userPoolClientId: string;
  readonly dispatch: (action: { readonly type: string }) => unknown;
  readonly navigate: (path: string) => void;
}

/**
 * Run the full callback pipeline: load verifier → exchange → decode
 * → persist → dispatch → getProfile → navigate.
 *
 * Returns the displayable error message ('') on success, or a
 * non-empty error string after dispatching `authSignInFailed`.
 */
export async function runOAuthCallback(
  args: CallbackPipelineArgs,
): Promise<string> {
  const verifier = loadCodeVerifier(args.userPoolClientId);
  if (!verifier) {
    args.dispatch(authSignInFailed({ error: 'missing-verifier' }));
    return 'missing-verifier';
  }
  try {
    const redirectUri = `${window.location.origin}/oauth-callback`;
    const tokens = await exchangeCodeForTokens({
      hostedUiUrl: args.hostedUiUrl,
      userPoolClientId: args.userPoolClientId,
      code: args.code,
      redirectUri,
      codeVerifier: verifier,
    });
    return await finishCallback({ ...args, tokens });
  } catch (err) {
    const message = err instanceof Error ? err.message : 'token-exchange-failed';
    args.dispatch(authSignInFailed({ error: message }));
    return message;
  }
}

interface FinishCallbackArgs extends CallbackPipelineArgs {
  readonly tokens: CognitoTokenResponse;
}

/**
 * Tail of the callback pipeline once the token exchange has
 * succeeded — decode, persist, dispatch, getProfile, navigate.
 */
async function finishCallback(args: FinishCallbackArgs): Promise<string> {
  const payload = decodeJwt(args.tokens.id_token);
  if (!payload || typeof payload.sub !== 'string') {
    args.dispatch(authSignInFailed({ error: 'invalid-id-token' }));
    return 'invalid-id-token';
  }
  storeTokens(args.userPoolClientId, {
    idToken: args.tokens.id_token,
    accessToken: args.tokens.access_token,
    refreshToken: args.tokens.refresh_token,
    expiresAt: Date.now() + args.tokens.expires_in * 1000,
  });
  args.dispatch(
    authSignedIn({
      idToken: args.tokens.id_token,
      accessToken: args.tokens.access_token,
      cognitoSub: payload.sub,
    }),
  );
  clearCodeVerifier(args.userPoolClientId);
  const profile = await getProfile(args.tokens.id_token);
  args.navigate(profile.exists ? '/' : '/sign-up');
  return '';
}

/**
 * Render a status message — either the in-flight progress
 * indicator or the failure alert, depending on `error`.
 */
function CallbackStatus({ error }: { readonly error: string }): JSX.Element {
  if (error) {
    return (
      <Alert severity="error" role="alert">
        Sign-in failed ({error}). Please try again.
      </Alert>
    );
  }
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
      <CircularProgress size={24} />
      <Typography>Completing sign-in…</Typography>
    </Stack>
  );
}

/**
 * OAuth-callback route component. Reads `?code=...`, runs the
 * callback pipeline once on mount, and renders either the
 * in-flight progress indicator or the failure alert.
 */
export default function OAuthCallbackPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string>('');
  const code = searchParams.get('code') ?? '';
  const { userPoolClientId, hostedUiUrl } = getCognitoConfig();

  useEffect(() => {
    if (!code) {
      dispatch(authSignInFailed({ error: 'missing-code' }));
      setError('missing-code');
      return;
    }
    void runOAuthCallback({
      code,
      hostedUiUrl,
      userPoolClientId,
      dispatch,
      navigate,
    }).then((result) => {
      if (result) setError(result);
    });
  }, [code, hostedUiUrl, userPoolClientId, dispatch, navigate]);

  return (
    <Box sx={{ p: 4 }}>
      <SeoHead
        route="/oauth-callback"
        pageTitle="Signing in..."
        branding={brandingFixture.share}
      />
      <CallbackStatus error={error} />
    </Box>
  );
}
